import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import costGroupService from "@/services/mrfiktiv/services/costGroupService";
import { MrfiktivCostGroupViewModelGen, MrfiktivUpdateCostGroupDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import { CostGroupDataAccessLayer } from "@/store/modules/access-layers/cost-group.access-layer";
import { ITimestamp, Timestamp } from "./timestamp.entity";

@IsFilterable
class CostGroupBase implements IEntity<MrfiktivCostGroupViewModelGen, MrfiktivUpdateCostGroupDtoGen> {
  /** Id of the cost group */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.id"
  })
  id: string;

  /** The partnerId of the cost */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.partnerId"
  })
  partnerId: string;

  /** Title of the cost group */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.title"
  })
  title: string;

  /** Internal description of the cost group */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.description"
  })
  description: string;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  constructor(data: Partial<MrfiktivCostGroupViewModelGen>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.title = data.title ?? "";
    this.description = data.description ?? "";
    this.timestamp = data.timestamp ?? { created: "", lastModified: "" };
  }

  map(data: MrfiktivCostGroupViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.title = data.title;
    this.description = data.description;
    this.timestamp = data.timestamp;
  }

  async create(): Promise<this> {
    const res = await costGroupService.create(this.partnerId, {
      title: this.title,
      description: this.description
    });

    this.map(res);
    CostGroupDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    const res = await costGroupService.getOne(this.partnerId, this.id);

    this.map(res);
    CostGroupDataAccessLayer.set(this);

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateCostGroupDtoGen>): Promise<this> {
    const res = await costGroupService.update(this.partnerId, this.id, dto);

    this.map(res);
    CostGroupDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    return this.updatePartial({
      title: this.title,
      description: this.description
    });
  }

  async delete(): Promise<void> {
    const res = await costGroupService.delete(this.partnerId, this.id);

    this.map(res);
    CostGroupDataAccessLayer.delete(this);
  }
}

type ICostGroup = CostGroupBase;
const CostGroup = Filter.createForClass(CostGroupBase);

export { CostGroup, ICostGroup };
